import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { HeroOrb } from "./HeroOrb";
import { useT } from "../lib/prefs";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const t = useT();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const desk = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
  const yText = useTransform(scrollYProgress, [0, 1], [0, desk ? 140 : 0]);
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, desk ? -80 : 0]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-24 sm:pt-28 pb-16 overflow-hidden grid-bg"
    >
      <div className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-violet-600/25 blur-[140px]" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-fuchsia-600/15 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease }}
            className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] text-violet-200 border border-violet-400/30 rounded-full px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 bg-violet-300 rounded-full" /> RAHSIA TRADING<span className="hidden sm:inline">{t.heroBadgeExtra}</span>
          </motion.div>

          <h1 className="font-display font-black uppercase leading-[0.88] tracking-tight text-[16vw] sm:text-[11vw] lg:text-[7.4rem]" data-testid="hero-title">
            <Line delay={0.4} className="text-white">Private</Line>
            <Line delay={0.55} className="text-gradient">Class</Line>
          </h1>

          <div className="mt-8 font-display font-bold uppercase text-2xl sm:text-4xl lg:text-5xl leading-tight" data-testid="hero-subtitle">
            <Line delay={0.8} className="text-gradient">{t.noFee}</Line>
            <Line delay={0.95} className="text-gradient">{t.noDeposit}</Line>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease }}
            className="mt-8 max-w-md text-base text-violet-100/80 leading-relaxed"
          >
            {t.heroText}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={() => scrollToId("booking")}
              className="btn-neon group inline-flex items-center justify-between sm:justify-start gap-3 rounded-full pl-7 pr-3 py-3 font-semibold w-full sm:w-auto"
            >
              {t.heroCta}
              <span className="w-10 h-10 rounded-full bg-white text-violet-700 grid place-items-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="w-5 h-5" />
              </span>
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yOrb }} className="lg:col-span-5 relative mt-6 lg:mt-0 h-[440px] sm:h-[540px] lg:h-[600px]" data-testid="hero-visual">
          <HeroOrb rx={rx} ry={ry} />
        </motion.div>
      </div>
    </section>
  );
};
